import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../../hooks/useBoardChartData';
import { BaseChartProps } from '../../charts/types';
import { format, eachDayOfInterval, startOfWeek, endOfWeek, startOfMonth, endOfMonth, getWeek, startOfDay } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { getCurrencySymbol, formatCurrency } from '../../charts/chartTheme';

const NetLinearTrendGraph: React.FC<{ days: Date[], netPerDay: Record<string, number> }> = ({ days, netPerDay }) => {
    const values = days.map(d => netPerDay[format(d, 'yyyy-MM-dd')] || 0);
    const maxVal = Math.max(...values, 100);
    const minVal = Math.min(...values, -100);
    const range = maxVal - minVal;

    const columnWidth = 120;
    const height = 150;
    const paddingY = 20;
    const width = days.length * columnWidth;

    const getX = (i: number) => (i + 0.5) * columnWidth;
    const getY = (v: number) => height - paddingY - ((v - minVal) / range) * (height - 2 * paddingY);

    const zeroY = getY(0);

    // Generate smooth cubic bezier curve points
    const points = values.map((v, i) => ({ x: getX(i), y: getY(v) }));

    const renderPath = () => {
        if (points.length < 2) return "";
        let d = `M ${points[0].x},${points[0].y}`;
        for (let i = 0; i < points.length - 1; i++) {
            const curr = points[i];
            const next = points[i + 1];
            const cp1x = curr.x + (next.x - curr.x) / 2;
            const cp2x = curr.x + (next.x - curr.x) / 2;
            d += ` C ${cp1x},${curr.y} ${cp2x},${next.y} ${next.x},${next.y}`;
        }
        return d;
    };

    const pathData = renderPath();
    const zeroPosPercent = (zeroY / height) * 100;

    return (
        <div className="relative overflow-visible" style={{ width: width }}>
            <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible block">
                <defs>
                    <linearGradient id="trendLineGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" />
                        <stop offset={`${Math.max(0, zeroPosPercent - 25)}%`} stopColor="#10b981" />
                        <stop offset={`${Math.min(100, zeroPosPercent + 25)}%`} stopColor="#ef4444" />
                        <stop offset="100%" stopColor="#ef4444" />
                    </linearGradient>
                </defs>
                <line x1="0" y1={zeroY} x2={width} y2={zeroY} stroke="currentColor" strokeWidth="1" strokeDasharray="6 4" className="text-neutral-200 dark:text-neutral-800" />
                <path
                    d={pathData}
                    fill="none"
                    stroke="url(#trendLineGradient)"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="transition-all duration-700 ease-in-out"
                />
                {points.map((p, i) => (
                    <g key={i} className="group/node">
                        <circle cx={p.x} cy={p.y} r="10" fill="transparent" />
                        <circle
                            cx={p.x}
                            cy={p.y}
                            r="4.5"
                            className="fill-white dark:fill-neutral-900 stroke-emerald-500 dark:stroke-emerald-400 stroke-2 transition-all duration-300"
                        />
                        <circle
                            cx={p.x}
                            cy={p.y}
                            r="1.5"
                            className={`${values[i] >= 0 ? 'fill-emerald-500' : 'fill-red-500'} transition-all duration-300`}
                        />
                    </g>
                ))}
            </svg>
        </div>
    );
};

export const DailyBreakdownTable: React.FC<BaseChartProps & { forceViewMode?: 'weeks' | 'months' | 'all', forcePeriodIndex?: number, hideFooter?: boolean }> = ({ forceViewMode, forcePeriodIndex, hideFooter }) => {
    const { t, i18n: i18nInstance } = useTranslation('common');
    const { finance, currencySettings, selectedEntities, channels, entities, journals, specs, dateRange } = useBoardChartData();
    const [localViewMode, setLocalViewMode] = useState<'weeks' | 'months' | 'all'>('months');
    const viewMode = forceViewMode || localViewMode;
    const [localSelectedPeriodIndex, setLocalSelectedPeriodIndex] = useState<number>(0);
    const selectedPeriodIndex = forcePeriodIndex !== undefined ? forcePeriodIndex : localSelectedPeriodIndex;
    const setSelectedPeriodIndex = forcePeriodIndex !== undefined ? () => {} : setLocalSelectedPeriodIndex;

    const currentLocale = i18nInstance.language === 'fr' ? fr : enUS;
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const { periods, tableData, netPerDay } = useMemo(() => {
        let start = dateRange.startDate ? startOfDay(dateRange.startDate) : undefined;
        let end = dateRange.endDate ? startOfDay(dateRange.endDate) : undefined;

        if (!start || !end) {
            const today = startOfDay(new Date());
            start = startOfMonth(today);
            end = endOfMonth(today);
        }

        const intervalDays = eachDayOfInterval({ start, end });
        const allDaysObj: Record<string, boolean> = {};
        intervalDays.forEach(d => allDaysObj[format(d, 'yyyy-MM-dd')] = true);

        const generatedPeriods: { title: string, days: Date[] }[] = [];
        if (viewMode === 'weeks') {
            const weeksSet = new Set<string>();
            intervalDays.forEach(day => {
                const wStart = startOfWeek(day, { weekStartsOn: 1 });
                const wEnd = endOfWeek(day, { weekStartsOn: 1 });
                const weekStr = `${format(wStart, 'yyyy-MM-dd')}_${format(wEnd, 'yyyy-MM-dd')}`;
                if (!weeksSet.has(weekStr)) {
                    weeksSet.add(weekStr);
                    const weekNum = getWeek(day, { weekStartsOn: 1 });
                    const periodDays = eachDayOfInterval({ start: wStart, end: wEnd }).filter(d => allDaysObj[format(d, 'yyyy-MM-dd')]);
                    if (periodDays.length > 0) {
                        generatedPeriods.push({ title: t('charts.components.week_number', { number: weekNum, defaultValue: `Week ${weekNum}` }), days: periodDays });
                    }
                }
            });
        } else if (viewMode === 'months') {
            const monthsSet = new Set<string>();
            intervalDays.forEach(day => {
                const mStart = startOfMonth(day);
                const mEnd = endOfMonth(day);
                const monthStr = format(mStart, 'yyyy-MM');
                if (!monthsSet.has(monthStr)) {
                    monthsSet.add(monthStr);
                    const periodDays = eachDayOfInterval({ start: mStart, end: mEnd }).filter(d => allDaysObj[format(d, 'yyyy-MM-dd')]);
                    if (periodDays.length > 0) {
                        generatedPeriods.push({ title: format(day, 'MMMM yyyy', { locale: currentLocale }), days: periodDays });
                    }
                }
            });
        }

        const dailyData = finance?.dailyBreakdown || {};
        const entityMap: Record<string, any> = {};
        if (entities) entities.forEach((e: any) => { entityMap[e.id] = e; });

        // Map normal channels by their underlying channelId; pseudo rows are
        // resolved separately via the entity -> BoardChannel.id map below.
        const channelMap: Record<string, any> = {};
        if (channels) {
            channels.forEach((c: any) => {
                if (!c.isPseudoChannel) {
                    channelMap[c.channelId ?? c.id] = c;
                }
            });
        }

        // Build entity -> pseudo BoardChannel.id map (descendants of each
        // pseudo's source entity all attribute to that pseudo's row).
        const entityToPseudoBoardChannelId: Record<string, string> = {};
        if (channels && entities) {
            channels.forEach((c: any) => {
                if (!c.isPseudoChannel || !c.sourceEntityId) return;
                const stack: string[] = [String(c.sourceEntityId)];
                while (stack.length) {
                    const id = stack.pop()!;
                    entityToPseudoBoardChannelId[id] = c.id;
                    entities.forEach((e: any) => {
                        if (String(e.parentId) === id) stack.push(String(e.id));
                    });
                }
            });
        }

        const journalLookup: Record<string, string> = {};
        if (journals) journals.forEach((j: any) => { journalLookup[j.id] = j.name || t('charts.components.moneyJournal', 'Money Journal'); });

        const getChannelCategory = (channel: any): 'ads' | 'sales' => {
            const cat = channel?.channelTypeConfig?.category?.toLowerCase() || '';
            if (cat === 'advertising' || cat === 'marketing' || cat === 'ads') return 'ads';
            return 'sales';
        };

        type RowEntry = { name: string; type: 'ads' | 'sales' | 'journal' | 'spec'; dates: Record<string, number> };
        const rowData: Record<string, RowEntry> = {};
        const computedNetPerDay: Record<string, number> = {};

        // Iterate through pre-aggregated daily data
        Object.entries(dailyData).forEach(([dateStr, dayData]: [string, any]) => {
            if (!allDaysObj[dateStr]) return;
            
            computedNetPerDay[dateStr] = dayData.metrics?.net || 0;

            dayData.breakdown.forEach((item: any) => {
                const val = item.type === 'EXPENSE' ? -Math.abs(item.value) : Math.abs(item.value);
                const entityId = item.entityId ? String(item.entityId) : '';
                
                let rowId = '';
                let type: 'ads' | 'sales' | 'journal' | 'spec' = 'sales';
                let name = item.entityName || 'Unknown';

                if (entityId.startsWith('journal-')) {
                    const jId = entityId.replace('journal-', '');
                    rowId = `journal_${jId}`;
                    type = 'journal';
                    name = journalLookup[jId] || item.entityName || 'Money Journal';
                } else if (entityId.startsWith('spec-')) {
                    const sId = entityId.replace('spec-', '');
                    rowId = `spec_${sId}`;
                    type = 'spec';
                    name = specs?.find((s: any) => s.id === sId)?.name || item.entityName || t('charts.components.boardSpec', 'Board Spec');
                } else {
                    // Channel-based entity. If it falls under a pseudo-channel's
                    // scope, attribute to that pseudo's BoardChannel row; otherwise
                    // attribute to the underlying real channel.
                    const pseudoBoardChannelId = entityToPseudoBoardChannelId[String(entityId)];
                    if (pseudoBoardChannelId) {
                        const pseudoChannel = (channels || []).find((c: any) => c.id === pseudoBoardChannelId);
                        type = getChannelCategory(pseudoChannel);
                        rowId = `channel_${pseudoBoardChannelId}`;
                        name = pseudoChannel?.name || t('charts.components.channel', 'Channel');
                    } else {
                        const channel = channelMap[item.channelId];
                        type = getChannelCategory(channel);
                        rowId = `channel_${item.channelId}`;
                        name = channel?.name || t('charts.components.channel', 'Channel');
                    }
                }

                if (!rowData[rowId]) rowData[rowId] = { name, type, dates: {} };
                rowData[rowId].dates[dateStr] = (rowData[rowId].dates[dateStr] || 0) + val;
            });
        });

        const categorized = { ads: [] as RowEntry[], sales: [] as RowEntry[], journal: [] as RowEntry[], spec: [] as RowEntry[] };
        Object.values(rowData).forEach(r => {
            if (r.type === 'ads') categorized.ads.push(r);
            else if (r.type === 'sales') categorized.sales.push(r);
            else if (r.type === 'journal') categorized.journal.push(r);
            else if (r.type === 'spec') categorized.spec.push(r);
        });

        return { periods: generatedPeriods, tableData: categorized, netPerDay: computedNetPerDay };
    }, [finance, selectedEntities, channels, entities, journals, specs, dateRange, viewMode, t, currentLocale]);

    const activePeriod = useMemo(() => {
        if (viewMode === 'all') {
            const start = dateRange.startDate ? startOfDay(dateRange.startDate) : startOfMonth(new Date());
            const end = dateRange.endDate ? startOfDay(dateRange.endDate) : endOfMonth(new Date());
            return { title: t('charts.components.all', 'All'), days: eachDayOfInterval({ start, end }) };
        }
        return periods[selectedPeriodIndex] || { title: '', days: [] };
    }, [viewMode, periods, selectedPeriodIndex, dateRange, t]);
    const visibleDays = activePeriod.days;

    const renderRows = (rows: { name: string; dates: Record<string, number> }[], title: string) => {
        if (rows.length === 0) return null;
        return (
            <>
                <tr>
                    <td className="bg-neutral-50 dark:bg-neutral-800 px-4 py-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300 border-y border-neutral-200 dark:border-neutral-700 sticky left-0 z-10 w-[150px] min-w-[120px] max-w-[180px]">
                        {title}
                    </td>
                    <td colSpan={visibleDays.length} className="bg-neutral-50 dark:bg-neutral-800 border-y border-neutral-200 dark:border-neutral-700"></td>
                    <td className="bg-neutral-50 dark:bg-neutral-800 border-y border-neutral-200 dark:border-neutral-700 sticky right-0 z-10 shadow-[-1px_0_0_0_#f0f0f0] dark:shadow-[-1px_0_0_0_#262626]"></td>
                </tr>
                {rows.map((row, idx) => {
                    const periodRowTotal = visibleDays.reduce((sum, d) => sum + (row.dates[format(d, 'yyyy-MM-dd')] || 0), 0);
                    return (
                        <tr key={`${title}_${idx}`} className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 group">
                            <td className="px-4 py-2 text-xs font-medium text-neutral-700 dark:text-neutral-300 sticky left-0 bg-white dark:bg-neutral-900 shadow-[1px_0_0_0_#f0f0f0] dark:shadow-[1px_0_0_0_#262626] z-10 w-[150px] min-w-[120px] max-w-[180px] truncate" title={row.name}>
                                {row.name}
                            </td>
                            {visibleDays.map(d => {
                                const dateStr = format(d, 'yyyy-MM-dd');
                                const val = row.dates[dateStr] || 0;
                                return (
                                    <td key={dateStr} className={`px-4 py-2 text-xs text-right whitespace-nowrap w-[120px] min-w-[120px] max-w-[120px] ${val < 0 ? 'text-red-500' : val > 0 ? 'text-green-600 dark:text-green-500' : 'text-neutral-400 dark:text-neutral-600'}`}>
                                        {val === 0 ? '-' : (val < 0 ? `-${formatCurrency(Math.abs(val), currencySymbol)}` : formatCurrency(val, currencySymbol))}
                                    </td>
                                );
                            })}
                            <td className="px-4 md:px-6 py-2 text-xs text-right bg-white dark:bg-neutral-900 sticky right-0 z-10 shadow-[-1px_0_0_0_#f0f0f0] dark:shadow-[-1px_0_0_0_#262626] group-hover:bg-neutral-50 dark:group-hover:bg-neutral-800/80">
                                <div className={`font-black ${periodRowTotal < 0 ? 'text-red-500' : periodRowTotal > 0 ? 'text-green-600 dark:text-green-500' : 'text-neutral-400 dark:text-neutral-600'}`}>
                                    {periodRowTotal === 0 ? '-' : (periodRowTotal < 0 ? `-${formatCurrency(Math.abs(periodRowTotal), currencySymbol)}` : formatCurrency(periodRowTotal, currencySymbol))}
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </>
        );
    };

    return (
        <div className="w-full h-full flex flex-col bg-transparent overflow-hidden">
            <div className="flex-1 overflow-auto relative custom-scrollbar">
                <table className="text-left border-collapse min-w-max">
                    <thead className="sticky top-0 z-20 bg-white dark:bg-neutral-900 shadow-[0_1px_0_0_#f0f0f0] dark:shadow-[0_1px_0_0_#262626]">
                        <tr>
                            <th className="px-4 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 sticky left-0 bg-white dark:bg-neutral-900 shadow-[1px_0_0_0_#f0f0f0] dark:shadow-[1px_0_0_0_#262626] z-30 w-[150px] min-w-[120px] max-w-[180px]">
                                {t('charts.components.categoriesHeading', 'Categories')}
                            </th>
                            {visibleDays.map(d => (
                                <th key={format(d, 'yyyy-MM-dd')} className="px-4 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 text-right w-[120px] min-w-[120px] max-w-[120px]">
                                    {format(d, 'MMM dd', { locale: currentLocale })}
                                    <div className="text-[10px] text-neutral-400 dark:text-neutral-500 font-normal mt-0.5">{format(d, 'EEE', { locale: currentLocale })}</div>
                                </th>
                            ))}
                            <th className="px-4 md:px-6 py-3 text-[10px] md:text-xs font-black uppercase tracking-normal md:tracking-widest text-accent-primary text-right bg-white dark:bg-neutral-900 sticky right-0 z-30 shadow-[-1px_0_0_0_#f0f0f0] dark:shadow-[-1px_0_0_0_#262626]">
                                <span className="md:hidden">{t('charts.components.totalCaps', 'TOTAL')}</span>
                                <span className="hidden md:inline">{t('charts.components.periodTotalCaps', 'PERIOD TOTAL')}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-neutral-900">
                        {renderRows(tableData.ads, t('charts.components.ads', 'Ads'))}
                        {renderRows(tableData.sales, t('charts.components.sales', 'Sales'))}
                        {renderRows(tableData.journal, t('charts.components.moneyJournals', 'Money Journals'))}
                        {renderRows(tableData.spec, t('charts.components.boardSpecs', 'Board Specs'))}
                        <tr className="group">
                            <td className="px-4 py-3 text-xs font-bold text-neutral-800 dark:text-neutral-200 sticky left-0 bg-neutral-50 dark:bg-neutral-800 shadow-[1px_0_0_0_#e5e5e5] dark:shadow-[1px_0_0_0_#404040] z-10 border-t-2 border-neutral-200 dark:border-neutral-700 w-[150px] min-w-[120px] max-w-[180px]">
                                {t('charts.components.netProfit', 'Net Profit')}
                            </td>
                            {visibleDays.map(d => {
                                const dateStr = format(d, 'yyyy-MM-dd');
                                const val = netPerDay[dateStr] || 0;
                                return (
                                    <td key={dateStr} className={`px-4 py-3 text-xs text-right font-bold border-t-2 border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 whitespace-nowrap w-[120px] min-w-[120px] max-w-[120px] ${val < 0 ? 'text-red-600' : val > 0 ? 'text-green-600 dark:text-green-500' : 'text-neutral-500 dark:text-neutral-400'}`}>
                                        {val < 0 ? `-${formatCurrency(Math.abs(val), currencySymbol)}` : formatCurrency(val, currencySymbol)}
                                    </td>
                                );
                            })}
                            <td className="px-4 md:px-6 py-3 text-xs text-right bg-neutral-50 dark:bg-neutral-800 sticky right-0 z-10 shadow-[-1px_0_0_0_#e5e5e5] dark:shadow-[-1px_0_0_0_#404040] border-t-2 border-neutral-200 dark:border-neutral-700 group-hover:bg-neutral-100 dark:group-hover:bg-neutral-700/80">
                                <div className="font-black text-accent-primary">
                                    {(() => {
                                        const totalNet = visibleDays.reduce((sum, d) => sum + (netPerDay[format(d, 'yyyy-MM-dd')] || 0), 0);
                                        return totalNet < 0 ? `-${formatCurrency(Math.abs(totalNet), currencySymbol)}` : formatCurrency(totalNet, currencySymbol);
                                    })()}
                                </div>
                            </td>
                        </tr>
                        <tr className="h-44">
                            <td className="sticky left-0 bg-white dark:bg-neutral-900 z-10 shadow-[1px_0_0_0_#f0f0f0] dark:shadow-[1px_0_0_0_#262626] px-4 py-3 align-top w-[150px] min-w-[120px] max-w-[180px]">
                                <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{t('charts.components.trend', 'Trend')}</span>
                            </td>
                            <td colSpan={visibleDays.length + 1} className="p-0 relative bg-white dark:bg-neutral-900 border-t border-neutral-100 dark:border-neutral-800">
                                <div className="w-full">
                                    <NetLinearTrendGraph days={visibleDays} netPerDay={netPerDay} />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {!hideFooter && (
                <div className="px-4 py-2 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex items-center justify-between shrink-0 z-20">
                <div className="flex-1 overflow-x-auto scrollbar-hide flex items-center gap-2 mr-6">
                    {periods.map((p, idx) => (
                        <button
                            key={idx}
                            onClick={() => setSelectedPeriodIndex(idx)}
                            className={`whitespace-nowrap px-4 py-1.5 text-xs font-medium rounded-full transition-all flex-shrink-0 border ${selectedPeriodIndex === idx ? 'bg-neutral-800 text-white border-neutral-800 dark:bg-white dark:text-neutral-900 dark:border-white shadow-sm' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'}`}>{p.title}</button>
                    ))}
                </div>
                {!forceViewMode && (
                    <div className="flex bg-neutral-100 dark:bg-neutral-800 p-0.5 rounded-lg border border-neutral-200 dark:border-neutral-700 shrink-0">
                        <button className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'all' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`} onClick={() => { setLocalViewMode('all'); setSelectedPeriodIndex(0); }}>{t('charts.components.all', 'All')}</button>
                        <button className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'weeks' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`} onClick={() => { setLocalViewMode('weeks'); setSelectedPeriodIndex(0); }}>{t('charts.components.weeks')}</button>
                        <button className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${viewMode === 'months' ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`} onClick={() => { setLocalViewMode('months'); setSelectedPeriodIndex(0); }}>{t('charts.components.months')}</button>
                    </div>
                )}
                </div>
            )}
        </div>
    );
};
